/* eslint-disable react-hooks/exhaustive-deps */
import { tableScoreColors } from "@_common/colors";
import { memo, useMemo, useState } from "react";
import { Popover, PopoverHeader, PopoverBody } from "reactstrap";
import { months } from "../Constant/Util";
import {
  EmailCellProps,
  LineItemFlatRow,
  UseDynamicColumnsParams,
} from "../Constant/MIS.interface";
import { NavLink } from "react-router-dom";

const getScoreColumn = (header: string, accessor: string) => ({
  Header: header,
  accessor,
  width: 120,
  Cell: ({ row }: any) => {
    const value = row.original?.[accessor];
    return (
      <div
        className="score-button"
        style={{
          backgroundColor: tableScoreColors(
            value != null ? parseFloat(value) : 0,
          ),
        }}
      >
        {value == null ? "- -" : `${value}%`}
      </div>
    );
  },
});

const scoreCellFill: React.CSSProperties = {
  width: "100%",
  height: "100%",
  minHeight: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const EmailCell = memo(({ value, rowIndex, prefix, title }: EmailCellProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const list = useMemo(() => {
    if (!value || value === "N/A") return [];
    if (Array.isArray(value)) {
      return value.filter(Boolean);
    }
    return value
      .split(/[,|]/)
      .map((e: string) => e.trim())
      .filter(Boolean);
  }, [value]);

  if (!list.length) return <span>-</span>;
  const id = `${prefix}-${rowIndex}`;
  return (
    <div>
      {list[0]}
      {list.length > 1 && (
        <>
          <span
            id={id}
            style={{ marginLeft: 5, cursor: "pointer", color: "blue" }}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
          >
            +{list.length - 1}
          </span>
          <Popover
            placement="bottom"
            isOpen={open}
            target={id}
            toggle={() => setOpen((p) => !p)}
          >
            <PopoverHeader>{title}</PopoverHeader>
            <PopoverBody>
              {list.slice(1).map((e, i) => (
                <div key={i}>{e}</div>
              ))}
            </PopoverBody>
          </Popover>
        </>
      )}
    </div>
  );
});

EmailCell.displayName = "EmailCell";

const makeEmailColumn = (header: string, accessor: string, title: string) => ({
  Header: header,
  accessor,
  Cell: ({ row }: any) => (
    <EmailCell
      value={row.original[accessor]}
      rowIndex={row.index}
      prefix={accessor.toLowerCase().replace(/\s+/g, "-")}
      title={title}
    />
  ),
});

const scoreKeys = {
  REGION: "critical_score_r",
  LOCATION: "critical_score",
  VENDOR: "critical_score",
  GROUP: "critical_score_g",
  RECTIFICATION_REPORT: "critical_score",
};

export const useDynamicColumns = ({
  viewType,
  currentMonthName,
  prevMonth1Name,
  prevMonth2Name,
  prevMonth3Name,
  allParams,
  year,
  selectedDate,
}: UseDynamicColumnsParams) => {
  const scoreColumns = useMemo(
    () => [
      getScoreColumn(`${currentMonthName} Score`, "score"),
      getScoreColumn(`${prevMonth1Name} Score`, "prev_month_1_score"),
      getScoreColumn(`${prevMonth2Name} Score`, "prev_month_2_score"),
      getScoreColumn(`${prevMonth3Name} Score`, "prev_month_3_score"),
    ],
    [currentMonthName, prevMonth1Name, prevMonth2Name, prevMonth3Name],
  );

  const viewColumns = useMemo(() => {
    const map: Record<string, any[]> = {
      REGION: [
        {
          Header: "Region Name",
          accessor: "region_name",
          Cell: ({ row }: any) => (
            <NavLink
              className="fw-bold text-decoration-underline"
              to={`/audit/mis-reports/task/list?${allParams()}&region_id=${row.original.region_id}`}
            >
              {row.values.region_name}
            </NavLink>
          ),
        },
        { Header: "Sites", accessor: "location" },
        ...(scoreKeys["REGION"]
          ? [getScoreColumn("Critical Score", scoreKeys["REGION"])]
          : []),
      ],

      GROUP: [
        {
          Header: "Account Name",
          accessor: "group_name",
          Cell: ({ row }: any) => (
            <NavLink
              className="fw-bold text-decoration-underline"
              to={`/audit/mis-reports/task/list?${allParams()}&group_id=${row.original.group_id}`}
            >
              {row.values.group_name}
            </NavLink>
          ),
        },
        { Header: "Sites", accessor: "location" },
        ...(scoreKeys["GROUP"]
          ? [getScoreColumn("Critical Score", scoreKeys["GROUP"])]
          : []),
      ],

      LOCATION: [
        {
          Header: "Site Name",
          accessor: "location_name",
          Cell: ({ row }: any) => (
            <NavLink
              className="fw-bold text-decoration-underline"
              to={`/audit/mis-reports/task/list?${allParams()}&location_id=${row.original.location_id}`}
            >
              {row.values.location_name}
            </NavLink>
          ),
        },
        { Header: "Location", accessor: "city_name" },
        { Header: "Account", accessor: "account_name" },
        { Header: "Region", accessor: "region_name" },
        { Header: "Total Vendors", accessor: "vendor" },
        ...(scoreKeys["LOCATION"]
          ? [getScoreColumn("Critical Score", scoreKeys["LOCATION"])]
          : []),
      ],

      VENDOR: [
        {
          Header: "Vendor Name",
          accessor: "vendor_name",
          width: 300,
          Cell: ({ row }: any) => (
            <NavLink
              className="fw-bold text-decoration-underline"
              to={`/audit/mis-reports/task/list?${allParams()}&vendor_id=${row.original.vendor_id}`}
            >
              {row.values.vendor_name}
            </NavLink>
          ),
        },
        { Header: "Sites", accessor: "location" },
        ...(scoreKeys["VENDOR"]
          ? [getScoreColumn("Critical Score", scoreKeys["VENDOR"])]
          : []),
        { Header: "Score", accessor: "score" },
      ],

      SITE_MIS: [
        { Header: "Region", accessor: "region_name" },
        { Header: "Account", accessor: "group_name" },
        { Header: "Site", accessor: "location_name" },
        { Header: "Site Address", accessor: "address1" },
        { Header: "State", accessor: "state_name" },
        { Header: "City", accessor: "city_name" },
        { Header: "Vendor Name", accessor: "vendor_name" },
        makeEmailColumn("Users Email", "vendor_user_emails", "Users Emails"),
        makeEmailColumn(
          "Site Lead Emails",
          "site_lead_emails",
          "Site Lead Emails",
        ),
        makeEmailColumn(
          "Account Lead Emails",
          "account_lead_emails",
          "Account Lead Emails",
        ),
      ],

      YEAR_HISTORY: [
        {
          Header: "Vendor Name",
          accessor: "vendor_name",
          width: 180,
          Cell: ({ row }: any) => (
            <NavLink
              className="fw-bold text-decoration-underline"
              to={`/audit/mis-reports/task/list?${allParams()}&vendor_id=${row.original.vendor_id}`}
            >
              {row.values.vendor_name}
            </NavLink>
          ),
        },
        ...(months || []).map((month) => ({
          Header: `${month} ${year}`,
          accessor: `${month.toLowerCase()}_score`,
          width: 80,
          Cell: ({ row }: any) => {
            const score = row.values?.[`${month.toLowerCase()}_score`] ?? null;
            const parsedScore = score !== null ? parseFloat(score) : null;
            return (
              <div
                className="score-button"
                style={{
                  backgroundColor: tableScoreColors(parsedScore ?? 0),
                }}
              >
                {parsedScore === null ? "- -" : `${parsedScore}%`}
              </div>
            );
          },
        })),
      ],

      COMPLIANCE_SCORECARD: [
        {
          Header: "Month of Audit",
          accessor: "month",
          width: 110,
          Cell: ({ value }: any) => {
            const yearValue =
              typeof year === "string"
                ? year
                : year
                  ? String(year)
                  : new Date().getFullYear();
            const label = `${months[value - 1]} ${yearValue}`;
            return (
              <NavLink
                className="fw-bold text-decoration-underline"
                to={`/audit/mis-reports/task/list?${allParams()}`}
              >
                {label}
              </NavLink>
            );
          },
        },
        {
          Header: "Total Sites",
          accessor: "total_sites",
          width: 100,
        },
        {
          Header: "Total Vendors",
          accessor: "total_vendors",
          width: 120,
        },
        {
          Header: "Absent",
          accessor: "sites_with_no_submissions_percentage",
          width: 150,
          Cell: ({ row }: any) => {
            const value =
              row.values?.sites_with_no_submissions_percentage ?? null;
            return (
              <div
                className="score-button"
                style={{
                  backgroundColor: tableScoreColors(
                    value ? parseFloat(value) : 0,
                  ),
                  width: "80px",
                }}
              >
                {row.original?.sites_with_no_submissions ?? ""} (
                {value === null ? "- -" : `${value}%`})
              </div>
            );
          },
        },
        {
          Header: "Critical",
          accessor: "critical_non_complied_percentage",
          width: 150,
          Cell: ({ row }: any) => {
            const value = row.values?.critical_non_complied_percentage ?? null;
            return (
              <div
                className="score-button"
                style={{
                  backgroundColor: tableScoreColors(
                    value ? parseFloat(value) : 0,
                  ),
                }}
              >
                {value === null ? "- -" : `${value}%`}
              </div>
            );
          },
        },
        {
          Header: "Full",
          accessor: "compliance_score",
          width: 150,
          Cell: ({ row }: any) => {
            const value = row.values?.compliance_score ?? null;
            return (
              <div
                className="score-button"
                style={{
                  backgroundColor: tableScoreColors(
                    value ? parseFloat(value) : 0,
                  ),
                }}
              >
                {value === null ? "- -" : `${value}%`}
              </div>
            );
          },
        },
        {
          Header: "Non Critical",
          accessor: "non_critical_non_complied_percentage",
          width: 150,
          Cell: ({ row }: any) => {
            const value =
              row.values?.non_critical_non_complied_percentage ?? null;
            return (
              <div
                className="score-button"
                style={{
                  backgroundColor: tableScoreColors(
                    value ? parseFloat(value) : 0,
                  ),
                }}
              >
                {value === null ? "- -" : `${value}%`}
              </div>
            );
          },
        },
      ],

      LINE_ITEM_WISE_REPORT: (() => {
        const bg = (t: string) =>
          t === "group"
            ? "#c5c9d2"
            : t === "subheader"
              ? "#f5f7fa"
              : "transparent";
        const fg = (t: string) =>
          t === "subheader" ? "#444" : t === "group" ? "#444" : "#444";
        const fw = (t: string) =>
          t === "group" || t === "subheader" ? 700 : 400;
        const baseCell = (t: string): React.CSSProperties => ({
          background: bg(t),
          color: fg(t),
          fontWeight: fw(t),
          padding: "12px 10px",
          height: "100%",
          display: "flex",
          alignItems: "center",
          whiteSpace: "nowrap" as const,
          overflow: "hidden",
          textOverflow: "ellipsis",
          borderBottom: "1px solid #dee2e6",
          borderRight: "1px solid #dee2e6",
        });
        const DATA_COLS = [
          { label: "Score", key: "score", isScore: true },
          { label: "Compliant", key: "complied", isScore: false },
          { label: "Non-Compliant", key: "non_complied", isScore: false },
          { label: "Not-Produced", key: "not_submitted", isScore: false },
          { label: "Not-Applicable", key: "not_applicable", isScore: false },
          { label: "Total", key: "total", isScore: false },
        ];
        return [
          {
            Header: "S.No.",
            accessor: "_sno",
            width: 70,
            Cell: ({ row }: any) => {
              const r: LineItemFlatRow = row.original;
              return (
                <div style={{ ...baseCell(r._type), justifyContent: "center" }}>
                  {r._type === "group" ? r._sno : ""}
                </div>
              );
            },
          },
          {
            Header: "Act Name",
            accessor: "_legislation_name",
            minWidth: 600,
            Cell: ({ row }: any) => {
              const r: LineItemFlatRow = row.original;
              if (r._type === "group") {
                return (
                  <div style={baseCell("group")}>
                    {r._legislation_name ?? ""}
                  </div>
                );
              }
              if (r._type === "subheader") {
                return <div style={baseCell("subheader")}>Item</div>;
              }
              const cp = r._checkpoint!;
              return (
                <div style={baseCell("item")}>
                  <NavLink
                    className="fw-bold text-decoration-underline text-primary"
                    to={(() => {
                      const nextParams = new URLSearchParams(window.location.search);
                      const currentCityId = nextParams.get("city_id");
                      if (currentCityId) {
                        nextParams.set("city_id", currentCityId);
                      }
                      nextParams.set("checkpoint_id", String(cp.checkpoint_id));
                      nextParams.set("legislation_id", String(r._legislation_id));
                      if (!nextParams.has("date") && selectedDate) {
                        nextParams.set("date", selectedDate);
                      }
                      return `/audit/mis-reports/task/list?${nextParams.toString()}`;
                    })()}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {cp.proof_of_compliance ||
                      cp.checkpoint_name ||
                      "View Detail"}
                  </NavLink>
                </div>
              );
            },
          },
          {
            Header: "Overall Score",
            accessor: "_overall_score",
            minWidth: 600,
            Cell: ({ row }: any) => {
              const r: LineItemFlatRow = row.original;
              if (r._type === "group") {
                const score = r._overall_score;
                return (
                  <div style={{ ...baseCell("group"), padding: 0 }}>
                    <div
                      style={{
                        flex: "0 0 100px",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <div
                        className="score-button"
                        style={{
                          ...scoreCellFill,
                          backgroundColor: tableScoreColors(score ?? 0),
                          borderRadius: 0,
                          width: "100%",
                        }}
                      >
                        {score == null ? "- -" : `${score}%`}
                      </div>
                    </div>
                  </div>
                );
              }
              if (r._type === "subheader") {
                return (
                  <div style={{ ...baseCell("subheader"), padding: 0, gap: 0 }}>
                    {DATA_COLS.map((col, idx) => (
                      <div
                        key={col.key}
                        style={{
                          flex: col.key === "score" ? "0 0 100px" : "1 1 0",
                          padding: "10px 8px",
                          fontWeight: 700,
                          color: "#212529",
                          borderLeft:
                            idx > 0 ? "1px solid rgba(0,0,0,0.1)" : "none",
                          whiteSpace: "nowrap",
                          textAlign: "center",
                        }}
                      >
                        {col.label}
                      </div>
                    ))}
                  </div>
                );
              }
              const cp = r._checkpoint!;
              return (
                <div style={{ ...baseCell("item"), padding: 0, gap: 0 }}>
                  {DATA_COLS.map((col, idx) => {
                    const raw = (cp as any)[col.key];
                    return (
                      <div
                        key={col.key}
                        style={{
                          flex: col.key === "score" ? "0 0 100px" : "1 1 0",
                          padding: col.key === "score" ? 0 : "8px 10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderLeft: idx > 0 ? "1px solid #dee2e6" : "none",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {col.isScore ? (
                          <div
                            className="score-button"
                            style={{
                              ...scoreCellFill,
                              backgroundColor: tableScoreColors(
                                raw != null ? parseFloat(raw) : 0,
                              ),
                              borderRadius: 0,
                              width: "100%",
                            }}
                          >
                            {raw == null ? "- -" : `${parseFloat(raw)}%`}
                          </div>
                        ) : (
                          (raw ?? 0)
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            },
          },
        ];
      })(),

      RECTIFICATION_REPORT: [
        {
          Header: "S.No.",
          accessor: "_sno",
          width: 70,
          Cell: ({ row }: any) => (
            <div className="text-center">{row.index + 1}</div>
          ),
        },
        {
          Header: "Site Name",
          accessor: "location_name",
          width: 220,
        },
        {
          Header: "Location",
          accessor: "city_name",
          width: 130,
        },
        {
          Header: "Vendor Name",
          accessor: "vendor_name",
          width: 220,
          Cell: ({ row }: any) => {
            const encodedRowData = encodeURIComponent(
              JSON.stringify({
                ...row.original,
                site_name: row.original.location_name,
                headcount: row.original.manpower || row.original.headcount,
              }),
            );
            return (
              <NavLink
                className="fw-bold text-decoration-underline"
                to={`/audit/audit-status/task/list/${row.original.location_id}?${allParams()}&vendor_id=${row.original.vendor_id}&view_type=RECTIFICATION_REPORT&rowData=${encodedRowData}`}
              >
                {row.original.vendor_name}
              </NavLink>
            );
          },
        },
        ...(scoreKeys["RECTIFICATION_REPORT"]
          ? [
              getScoreColumn(
                "Critical Score",
                scoreKeys["RECTIFICATION_REPORT"],
              ),
            ]
          : []),
        {
          Header: "Full Score",
          accessor: "full_score",
          width: 110,
          Cell: ({ value }: any) => (
            <div
              className="score-button text-center"
              style={{
                backgroundColor: tableScoreColors(
                  value != null ? parseFloat(value) : 0,
                ),
              }}
            >
              {value == null ? "- -" : `${value}%`}
            </div>
          ),
        },
        // {
        //   Header: "Total NC",
        //   accessor: "rect_nc_count",
        //   width: 90,
        //   Cell: ({ value }: any) => (
        //     <div className="text-center">{value ?? 0}</div>
        //   ),
        // },
        // {
        //   Header: "Total Rectification Filed",
        //   accessor: "total_rect_filed",
        //   width: 160,
        //   Cell: ({ value }: any) => (
        //     <div className="text-center">{value ?? 0}</div>
        //   ),
        // },
        // {
        //   Header: "Total Rectification Accepted",
        //   accessor: "total_rect_accepted",
        //   width: 180,
        //   Cell: ({ value }: any) => (
        //     <div className="text-center">{value ?? 0}</div>
        //   ),
        // },
      ],
    };

    return map[viewType] ?? [];
  }, [viewType, allParams, months, year]);

  return useMemo(() => {
    const isSiteMis = viewType === "SITE_MIS";
    const isYearHistory = viewType === "YEAR_HISTORY";
    const isCompliance = viewType === "COMPLIANCE_SCORECARD";
    const isLineItemWise = viewType === "LINE_ITEM_WISE_REPORT";
    const isRectification = viewType === "RECTIFICATION_REPORT";
    return [
      ...viewColumns,
      ...(!isSiteMis &&
      !isYearHistory &&
      !isCompliance &&
      !isLineItemWise &&
      !isRectification &&
      viewType !== "VENDOR"
        ? scoreColumns
        : []),
      ...(!isYearHistory && !isCompliance && !isLineItemWise && !isRectification
        ? [
            {
              Header: "Headcount",
              accessor: viewType === "SITE_MIS" ? "headcount" : "manpower",
            },
          ]
        : []),
    ];
  }, [viewColumns, scoreColumns, viewType]);
};
